import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import GeneralInfoTab from './tabs/GeneralInfoTab';
import DescriptionTab from './tabs/DescriptionTab';
import ImagesTab from './tabs/ImagesTab';
import VariationsTab from './tabs/VariationsTab';
import PricingTab from './tabs/PricingTab';
import LogisticsTab from './tabs/LogisticsTab';
import TaxDataTab from './tabs/TaxDataTab';
import MarketplacesTab from './tabs/MarketplacesTab';
import KitCompositionTab from './tabs/KitCompositionTab';
import MarketplacePreview from './MarketplacePreview';
import { productService } from '../../../services/productService';
import AdvertisementPreviewModal from './AdvertisementPreviewModal';

const ProductEditorModal = ({ product, isOpen, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [productData, setProductData] = useState({
    sku: product?.sku || `SKU-${Date.now()}`,
    name: product?.name || '',
    brand: product?.brand || '',
    category: product?.category || 'Brinquedos',
    supplier: product?.supplier || '',
    supplierState: product?.supplierState || product?.supplier_state || '',
    condition: product?.condition || 'novo',
    warranty: product?.warranty || '',
    ean: product?.ean || '',
    
    // Description
    description: product?.description || '',
    tags: product?.tags || '',
    
    // Images
    images: product?.images || [],
    videoUrl: product?.videoUrl || '',
    
    // Variations
    variations: product?.variations || [],
    
    // Pricing
    cost: product?.cost || '',
    price: product?.price || '',
    mercadolivreMarkup: product?.metadata?.mercadolivreMarkup || 15,
    shopeeMarkup: product?.metadata?.shopeeMarkup || 12,
    amazonMarkup: product?.metadata?.amazonMarkup || 18,
    magaluMarkup: product?.metadata?.magaluMarkup || 10,
    promotionalPrice: product?.promotionalPrice || '',
    promotionEndDate: product?.promotionEndDate || '',
    
    // Logistics
    weight: product?.weight || '',
    width: product?.width || '',
    height: product?.height || '',
    length: product?.length || '',
    boxWeight: product?.boxWeight || '',
    packagingType: product?.packagingType || 'caixa',
    stock: product?.stock || '',
    minStock: product?.minStock || '',
    manufacturingTime: product?.manufacturingTime || '',
    stockLocation: product?.stockLocation || '',
    
    // Tax Data
    ncm: product?.ncm || '',
    cest: product?.cest || '',
    origin: product?.origin || 'nacional',
    cfop: product?.cfop || '',
    
    // Marketplaces
    mercadolivreActive: product?.metadata?.mercadolivreActive || false,
    mercadolivreCategory: product?.mercadolivreCategory || product?.mercadolivre_category || '',
    mercadolivreListingType: product?.mercadolivreListingType || product?.mercadolivre_listing_type || 'gold_special',
    mercadolivreFreeShipping: product?.metadata?.mercadolivreFreeShipping || false,
    
    shopeeActive: product?.metadata?.shopeeActive || false,
    shopeeCategory: product?.shopeeCategory || product?.shopee_category || '',
    shopeeShippingType: product?.metadata?.shopeeShippingType || 'seller',
    
    amazonActive: product?.metadata?.amazonActive || false,
    magaluActive: product?.metadata?.magaluActive || false,
    
    syncStock: product?.metadata?.syncStock || true,
    syncPrice: product?.metadata?.syncPrice || true,
    
    ...product
  });

  const [validationErrors, setValidationErrors] = useState({});

  const [showPreview, setShowPreview] = useState(false);
  const [kitComponents, setKitComponents] = useState([]);

  // Load kit components if product is a kit
  useEffect(() => {
    if (product?.productType === 'kit' && product?.id) {
      loadKitComponents();
    }
  }, [product?.id, product?.productType]);

  const loadKitComponents = async () => {
    try {
      const kitWithComponents = await productService?.getKitWithComponents(product?.id);
      setKitComponents(kitWithComponents?.components || []);
    } catch (error) {
      console.error('Error loading kit components:', error);
      setKitComponents([]);
    }
  };

  const tabs = [
    { id: 'general', label: 'Informações Gerais', icon: 'Info' },
    { id: 'description', label: 'Descrição', icon: 'FileText' },
    { id: 'images', label: 'Imagens', icon: 'Image' },
    ...(productData?.productType === 'kit' ? [{ id: 'kitcomposition', label: 'Produtos do Kit', icon: 'Package' }] : []),
    { id: 'variations', label: 'Variações', icon: 'Grid' },
    { id: 'pricing', label: 'Precificação', icon: 'DollarSign' },
    { id: 'logistics', label: 'Logística', icon: 'Package' },
    { id: 'taxdata', label: 'Dados Fiscais', icon: 'FileCheck' },
    { id: 'marketplaces', label: 'Marketplaces', icon: 'Store' }
  ];

  const handleFieldChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  const validateProduct = () => {
    const errors = {};
    
    // Required fields
    if (!productData?.name) errors.name = 'Nome é obrigatório';
    if (!productData?.sku) errors.sku = 'SKU é obrigatório';
    if (!productData?.brand) errors.brand = 'Marca é obrigatória';
    if (!productData?.category) errors.category = 'Categoria é obrigatória';
    if (!productData?.price || parseFloat(productData?.price) <= 0) {
      errors.price = 'Preço deve ser maior que zero';
    }
    if (!productData?.weight || parseFloat(productData?.weight) <= 0) {
      errors.weight = 'Peso deve ser maior que zero';
    }
    if (!productData?.ncm || productData?.ncm?.length !== 8) {
      errors.ncm = 'NCM deve ter 8 dígitos';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateProduct()) {
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Preencha todos os campos obrigatórios',
          progress: 0
        }
      }));
      return;
    }
    
    try {
      setSaving(true);
      
      // Show saving feedback
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: 'Salvando alterações...',
          progress: 50
        }
      }));
      
      // Prepare data for save with all fields properly mapped
      const dataToSave = {
        ...productData,
        // Ensure numeric fields are properly parsed
        price: parseFloat(productData?.price) || 0,
        cost: parseFloat(productData?.cost) || 0,
        weight: parseFloat(productData?.weight) || 0,
        width: parseFloat(productData?.width) || 0,
        height: parseFloat(productData?.height) || 0,
        length: parseFloat(productData?.length) || 0,
        stock: parseInt(productData?.stock) || 0,
        
        // Ensure supplier fields are included
        supplierName: productData?.supplier || productData?.supplierName,
        supplierState: productData?.supplierState,
        
        // Preserve kit components and images
        images: productData?.images || [],
        
        // Ensure all metadata fields are preserved
        metadata: {
          ...(productData?.metadata || {}),
          mercadolivreMarkup: parseFloat(productData?.mercadolivreMarkup) || 15,
          shopeeMarkup: parseFloat(productData?.shopeeMarkup) || 12,
          amazonMarkup: parseFloat(productData?.amazonMarkup) || 18,
          magaluMarkup: parseFloat(productData?.magaluMarkup) || 10,
          mercadolivreActive: productData?.mercadolivreActive,
          mercadolivreFreeShipping: productData?.mercadolivreFreeShipping,
          shopeeActive: productData?.shopeeActive,
          shopeeShippingType: productData?.shopeeShippingType,
          amazonActive: productData?.amazonActive,
          magaluActive: productData?.magaluActive,
          syncStock: productData?.syncStock,
          syncPrice: productData?.syncPrice,
          // Preserve kit components if this is a kit
          kitComponents: productData?.metadata?.kitComponents || []
        }
      };
      
      // Log data being saved for debugging
      console.log('Saving product data:', dataToSave);
      
      // Update in Supabase
      const updatedProduct = await productService?.update(product?.id, dataToSave);
      
      if (!updatedProduct) {
        throw new Error('Falha ao atualizar produto no banco de dados');
      }
      
      console.log('Product saved successfully:', updatedProduct);
      
      // Show success feedback
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: 'Alterações salvas com sucesso',
          progress: 100
        }
      }));
      
      // Dispatch custom event to refresh product list
      window.dispatchEvent(new CustomEvent('productUpdated', {
        detail: { product: updatedProduct }
      }));
      
      // Call parent onSave callback
      if (onSave) {
        onSave(updatedProduct);
      }
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error saving product:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: error?.message || 'Erro ao salvar alterações. Verifique sua conexão e tente novamente.',
          progress: 0
        }
      }));
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <GeneralInfoTab
            productData={productData}
            onFieldChange={handleFieldChange}
            validationErrors={validationErrors}
          />
        );
      case 'description':
        return (
          <DescriptionTab
            productData={productData}
            onFieldChange={handleFieldChange}
          />
        );
      case 'images':
        return (
          <ImagesTab
            productData={productData}
            onFieldChange={handleFieldChange}
          />
        );
      case 'kitcomposition':
        return (
          <KitCompositionTab
            productData={productData}
          />
        );
      case 'variations':
        return (
          <VariationsTab
            productData={productData}
            onFieldChange={handleFieldChange}
          />
        );
      case 'pricing':
        return (
          <PricingTab
            productData={productData}
            onFieldChange={handleFieldChange}
            validationErrors={validationErrors}
          />
        );
      case 'logistics':
        return (
          <LogisticsTab
            productData={productData}
            onFieldChange={handleFieldChange}
            validationErrors={validationErrors}
          />
        );
      case 'taxdata':
        return (
          <TaxDataTab
            productData={productData}
            onFieldChange={handleFieldChange}
            validationErrors={validationErrors}
          />
        );
      case 'marketplaces':
        return (
          <MarketplacesTab
            productData={productData}
            onFieldChange={handleFieldChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[190] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[200] overflow-hidden flex items-center justify-center p-4">
        <div className="bg-background rounded-lg shadow-brand-xl w-full max-w-[95vw] h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {product?.id ? 'Editar Produto' : 'Novo Produto'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                SKU: {productData?.sku}
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border px-6 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 border-b-2 transition-smooth
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span className="text-sm">{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden flex">
            {/* Editor Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {renderTabContent()}
            </div>

            {/* Sidebar Preview */}
            <div className="w-80 border-l border-border overflow-y-auto">
              <MarketplacePreview productData={productData} />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </Button>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={16} className="mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
              
              <Button
                iconName="Upload"
                onClick={() => {
                  handleSave();
                  // Additional publish logic here
                }}
                disabled={saving}
              >
                Publicar em Marketplaces
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Advertisement Preview Modal */}
      <AdvertisementPreviewModal
        product={productData}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </>
  );
};

export default ProductEditorModal;